import { useEffect, useMemo, useState } from "react";
import type { ScopeResponse } from "@k8s-dashboard/contracts";
import {
  NAMESPACE_SELECTION_MAX_MEMBERS,
  canonicalNamespaceSelection,
  namespaceSelectionBinding,
  type NamespaceSelection,
} from "@/state/namespaceSelection";

export function NamespaceMultiSelector({
  scope,
  clusterId,
  selection,
  status,
  disabled,
  onApply,
  onRetry,
  labels,
}: {
  scope: ScopeResponse | undefined;
  clusterId: string;
  selection: NamespaceSelection | undefined;
  status: "pending" | "ready" | "repair" | "ack-lost";
  disabled?: boolean;
  onApply: (selection: NamespaceSelection) => void;
  onRetry: () => void;
  labels: Readonly<{
    label: string;
    all: string;
    apply: string;
    repair: string;
    acknowledgement: string;
    retry: string;
    selected: string;
    limit: string;
  }>;
}) {
  const cluster = scope?.clusters.find((candidate) => candidate.id === clusterId);
  const authorized = useMemo(() => {
    const values = cluster?.namespaces === "all" ? cluster.availableNamespaces ?? [] : cluster?.namespaces ?? [];
    return [...new Set(values)].sort();
  }, [cluster]);
  const [draftMode, setDraftMode] = useState<"all" | "selected">(selection?.mode ?? "selected");
  const [draftNames, setDraftNames] = useState<readonly string[]>(selection?.mode === "selected" ? selection.names : []);
  const [query, setQuery] = useState("");
  const committedBinding = selection ? namespaceSelectionBinding(selection) : "";

  useEffect(() => {
    setDraftMode(selection?.mode ?? "selected");
    setDraftNames(selection?.mode === "selected" ? selection.names : []);
  }, [committedBinding, selection]);

  const pinned = draftNames.filter((name) => authorized.includes(name));
  const needle = query.trim().toLowerCase();
  const matches = needle ? authorized.filter((name) => name.toLowerCase().includes(needle)) : authorized;
  const visible = [...pinned, ...matches.filter((name) => !pinned.includes(name))].slice(0, 40);
  const draft = canonicalNamespaceSelection(draftMode === "all"
    ? { mode: "all" }
    : { mode: "selected", names: draftNames });
  const toggle = (name: string) => {
    setDraftMode("selected");
    setDraftNames((current) => current.includes(name)
      ? current.filter((item) => item !== name)
      : current.length < NAMESPACE_SELECTION_MAX_MEMBERS ? [...current, name] : current);
  };

  return (
    <div className="scope namespace-multi">
      <span className="scope__label">{labels.label} :</span>
      <details>
        <summary>{selection?.mode === "all" ? labels.all : selection?.names.join(", ") || labels.selected}</summary>
        <div className="namespace-multi__panel">
          <label>
            <input type="radio" name="namespace-mode" checked={draftMode === "all"} onChange={() => setDraftMode("all")} />
            {labels.all}
          </label>
          <input
            type="search"
            value={query}
            aria-label={labels.label}
            placeholder={labels.selected}
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="namespace-multi__options" role="group" aria-label={labels.label}>
            {visible.map((name) => (
              <label key={name}>
                <input
                  type="checkbox"
                  checked={draftMode === "selected" && draftNames.includes(name)}
                  disabled={disabled || (draftNames.length >= NAMESPACE_SELECTION_MAX_MEMBERS && !draftNames.includes(name))}
                  onChange={() => toggle(name)}
                />
                {name}
              </label>
            ))}
            {authorized.length > visible.length && <span className="muted">+{authorized.length - visible.length}</span>}
          </div>
          <span className="muted">{labels.limit}</span>
          <button type="button" className="ds-button ds-button--sm" disabled={disabled || !draft} onClick={() => draft && onApply(draft)}>
            {labels.apply}
          </button>
        </div>
      </details>
      {status === "repair" && <span className="namespace-multi__error" role="alert">{labels.repair}</span>}
      {status === "ack-lost" && (
        <span className="namespace-multi__error" role="alert">
          {labels.acknowledgement}
          <button type="button" className="ds-button ds-button--sm" onClick={onRetry}>{labels.retry}</button>
        </span>
      )}
    </div>
  );
}
